import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { DestinationSection } from '../../../components/DestinationSection';
import { FAQSection } from '../../../components/FAQSection';
import { faqsPropTypes } from '../../../components/FAQSection/constants';
import { ManualDataSection } from '../../../components/ManualDataSection';
import { manualDataPropTypes } from '../../../components/ManualDataSection/constants';
import {
  Body,
  Breadcrumb,
  CustomH2,
  HTMLText,
  RowFlex,
  SectionCol,
  TableStyle,
  Wrapper,
} from '../../../components/SEO';
import { StickyNav } from '../../../components/StickyNav/StickyNav';
import SearchHeader from '../../SearchHeader';
import { DESTINATION } from '../../TravelSearchBar/constants';
import {
  companyServicesPropTypes,
  destinationCardsPropTypes,
  DESTINATIONS,
  FAQ,
  HISTORY,
  MANUAL_DATA,
  NEWS_FEED,
  SERVICES,
  siblingsPropTypes,
} from '../constants';
import { CompanyHistoryCard } from './CompanyHistoryCard';
import { CompanyPageTitleIntl } from './CompanyPageTitle';
import { CompanyServices } from './CompanyServices';
import {
  ChildLink,
  ChildLogo,
  Children,
  CompanySibling,
} from './CompanySibling';
import messages from './messages';

/**
 * @description Builds the sticky navigation content
 * @param {CompanyView.propTypes.data} data Data on the current company page
 * @returns {{id: string, text: string}[]}
 */
const getNavigation = (data) => {
  const navigation = [];
  if (data.services.length > 0) {
    navigation.push({
      id: SERVICES,
      text: <FormattedMessage {...messages[SERVICES]} />,
    });
  }

  if (data.newsFeed.length > 0) {
    navigation.push({
      id: NEWS_FEED,
      text: <FormattedMessage {...messages[NEWS_FEED]} />,
    });
  }

  if (data.faqs.length > 0) {
    navigation.push({
      id: FAQ,
      text: <FormattedMessage {...messages[FAQ]} />,
    });
  }

  if (data.destinationCards.length > 0) {
    navigation.push({
      id: DESTINATIONS,
      text: <FormattedMessage {...messages[DESTINATIONS]} />,
    });
  }

  navigation.push({
    id: HISTORY,
    text: <FormattedMessage {...messages[HISTORY]} />,
  });
  return navigation;
};

const CompanyView = ({
  companyHistoryLogo,
  data,
  destinationCards,
  getCitySuggestions,
  setDestinationCity,
  siblings,
}) => (
  <Wrapper customStyle={TableStyle}>
    <SearchHeader
      header={data.searchHeader.title}
      subHeader={data.searchHeader.subtitle}
    />
    <CompanyPageTitleIntl pathname={data.path} />
    <RowFlex>
      <StickyNav navigation={getNavigation(data)} />
      <Body>
        {data.breadcrumb && data.parentName && (
          <Breadcrumb path={data.breadcrumb} text={data.parentName} />
        )}
        {data.services.length > 0 && (
          <SectionCol id={SERVICES}>
            <CustomH2>{data.servicesHeader}</CustomH2>
            <CompanyServices data={data.services} />
          </SectionCol>
        )}

        {data.newsFeed.length > 0 && (
          <SectionCol id={NEWS_FEED}>
            <CustomH2>{data.newsFeedHeader}</CustomH2>
            <FAQSection faqs={data.newsFeed} open />
          </SectionCol>
        )}

        {data.manualData?.content?.length > 0 && (
          <SectionCol id={MANUAL_DATA}>
            <ManualDataSection manualData={data.manualData} open />
          </SectionCol>
        )}
        {data.faqs.length > 0 && (
          <SectionCol id={FAQ}>
            <CustomH2>{data.faqsHeader}</CustomH2>
            <FAQSection faqs={data.faqs} open />
          </SectionCol>
        )}

        {destinationCards.length > 0 && (
          <SectionCol id={DESTINATIONS}>
            <CustomH2>{data.destinationCardsHeader}</CustomH2>
            {destinationCards.map((destination) => (
              <DestinationSection
                key={destination.destinationCard.id}
                customClickHandler={() => {
                  setDestinationCity(
                    destination.destinationCard.url,
                    null,
                    DESTINATION,
                  );
                  getCitySuggestions(
                    DESTINATION,
                    destination.destinationCard.url,
                  );
                  window.scrollTo(0, 0);
                }}
                description={destination.description}
                destination={destination.destinationCard}
              />
            ))}
          </SectionCol>
        )}

        <SectionCol id={HISTORY}>
          <CustomH2>{data.historyHeader}</CustomH2>
          {data.history.website && (
            <CompanyHistoryCard
              contact={data.history.contact}
              logo={companyHistoryLogo}
              name={data.name}
              phone={data.history.phone}
              website={data.history.website}
            />
          )}
          <HTMLText
            dangerouslySetInnerHTML={{
              __html: data.history.description,
            }}
          />
        </SectionCol>
      </Body>
    </RowFlex>
    {siblings.length > 0 && (
      <RowFlex>
        <CompanySibling>
          <CustomH2>
            <FormattedMessage {...messages.seeAlso} />
          </CustomH2>
          <Children>
            {siblings.map((sibling) => (
              <ChildLink key={sibling.path} href={sibling.path}>
                <ChildLogo alt={sibling.name} src={sibling.logo} />
              </ChildLink>
            ))}
          </Children>
        </CompanySibling>
      </RowFlex>
    )}
  </Wrapper>
);

CompanyView.propTypes = {
  companyHistoryLogo: PropTypes.string,
  data: PropTypes.shape({
    breadcrumb: PropTypes.string,
    children: siblingsPropTypes,
    destinationCards: destinationCardsPropTypes,
    destinationCardsHeader: PropTypes.string,
    done: PropTypes.bool,
    faqs: faqsPropTypes,
    faqsHeader: PropTypes.string,
    history: PropTypes.shape({
      contact: PropTypes.string,
      description: PropTypes.string,
      image: PropTypes.string,
      phone: PropTypes.string,
      website: PropTypes.string,
    }),
    historyHeader: PropTypes.string,
    id: PropTypes.string,
    manualData: manualDataPropTypes,
    metaDescription: PropTypes.string,
    metaImage: PropTypes.string,
    metaTitle: PropTypes.string,
    name: PropTypes.string,
    newsFeed: faqsPropTypes,
    newsFeedHeader: PropTypes.string,
    parent: PropTypes.string,
    parentName: PropTypes.string,
    path: PropTypes.string,
    searchHeader: PropTypes.shape({
      backgroundAlt: PropTypes.string,
      backgroundImage: PropTypes.string,
      logoImage: PropTypes.string,
      subtitle: PropTypes.string,
      title: PropTypes.string,
    }),
    services: companyServicesPropTypes,
    servicesHeader: PropTypes.string,
    siblings: siblingsPropTypes,
    type: PropTypes.string,
  }),
  destinationCards: destinationCardsPropTypes,
  getCitySuggestions: PropTypes.func,
  searchHeaderBackground: PropTypes.string,
  setDestinationCity: PropTypes.func,
  siblings: siblingsPropTypes,
};

export { CompanyView };
